import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useI18n } from '@/contexts/I18nContext';
import { INote } from '@/features/calendar/hooks/useNotes';
import { getTechniqueName } from '@/shared/hooks/useTechniques';
import { Badge } from '@/components/ui/badge';
import { PartnerDisplay } from '@/components/ui/PartnerDisplay';
import { ExpandableText } from '@/components/ui/ExpandableText';

interface INoteCardProps {
  note: INote;
}

export const NoteCard: React.FC<INoteCardProps> = ({ note }) => {
  const { t, currentLanguage } = useI18n();
  const noteDate = new Date(note.created_at);
  const trainingDate = note.training ? new Date(note.training.start_time) : noteDate;

  const getNoteContent = (): string => {
    if (note.type === 'feedback' && note.feedback_notes) {
      return note.feedback_notes;
    }
    if (note.type === 'personal' && note.personal_notes) {
      return note.personal_notes;
    }
    return note.full_notes || '';
  };

  const getNoteTypeLabel = (): string => {
    switch (note.type) {
      case 'class':
        return t('calendar.my_notes.class_notes');
      case 'personal':
        return t('calendar.my_notes.personal_notes');
      case 'feedback':
        return t('calendar.my_notes.feedback');
      default:
        return '';
    }
  };

  const getLocalizedTrainingType = (type: string): string => {
    switch (type) {
      case 'Group Class':
        return t('calendar.group_class');
      case 'Private Class':
        return t('calendar.private_class');
      default:
        return type;
    }
  };

  return (
    <div className="flex gap-3 bg-[#281B1B] rounded-lg p-4 border border-white/10">
      {/* Date Column */}
      <div className="flex-shrink-0 w-12 text-center">
        <div className="text-xs font-semibold text-white/60">
          {(() => {
            const dayName = format(trainingDate, 'EEE', { locale: currentLanguage === 'pt' ? ptBR : undefined });
            const abbreviated = dayName.substring(0, 3);
            return abbreviated.charAt(0).toUpperCase() + abbreviated.slice(1);
          })()}
        </div>
        <div className="text-lg font-semibold text-white">
          {format(trainingDate, 'dd')}
        </div>
      </div>

      {/* Note Content */}
      <div className="flex-1 min-w-0">
        {/* Note Type Badge */}
        <div className="mb-2">
          <Badge 
            variant="outline"
            className="bg-white/10 text-white/80 border border-white/20 text-[10.5px] font-medium px-2 py-1 rounded font-roboto"
          >
            {getNoteTypeLabel()}
          </Badge>
        </div>

        {/* Training Info */}
        {note.training && (
          <div className="mb-2">
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
              {note.training.type 
                ? getLocalizedTrainingType(note.training.type)
                : (note.training.title === 'Group Class' || note.training.title === 'Private Class')
                  ? getLocalizedTrainingType(note.training.title)
                  : note.training.title
              }
              {note.training.modality && (
                <span className="text-white/60 font-normal">{note.training.modality}</span>
              )}
            </h3>
          </div>
        )}

        {/* Techniques */}
        {note.techniques && note.techniques.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {note.techniques.slice(0, 5).map((tech) => (
              <span
                key={tech.id}
                className="px-2 py-1 text-xs font-medium rounded-full bg-sossiety-red text-white border border-sossiety-red"
              >
                {getTechniqueName(tech, currentLanguage)}
              </span>
            ))}
            {note.techniques.length > 5 && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-sossiety-red text-white border border-sossiety-red">
                +{note.techniques.length - 5}
              </span>
            )}
          </div>
        )}

        {/* Training Partners */}
        {note.partners && note.partners.length > 0 && (
          <div className="mt-3 mb-2">
            <div className="text-xs text-white/60 mb-2">
              {note.is_instructor_class
                ? t('calendar.my_notes.gave_class_to')
                : t('calendar.my_notes.trained_with')}
            </div>
            <div className="flex flex-wrap gap-2">
              {note.partners.map((partner) => (
                <div key={partner.id} className="flex items-center gap-1.5">
                  <PartnerDisplay
                    partner={partner}
                    size="sm"
                    className="text-white"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Note Content */}
        <ExpandableText
          text={getNoteContent()}
          maxLength={300}
          className="text-sm text-white/80"
          buttonText={{
            expand: t('common.view_more'),
            collapse: t('common.view_less'),
          }}
        />
      </div>
    </div>
  );
};

